import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Send, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/messages/$id")({
  component: ConversationPage,
});

type Msg = { id: string; sender_id: string; body: string; created_at: string };

function ConversationPage() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<any[]>([]);
  const [title, setTitle] = useState("Conversation");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: m } = await supabase.from("messages").select("*").eq("conversation_id", id).order("created_at");
      setMsgs((m ?? []) as Msg[]);
      const { data: mem } = await supabase
        .from("conversation_members")
        .select("user_id")
        .eq("conversation_id", id);
      const ids = (mem ?? []).map((x: any) => x.user_id);
      const { data: profs } = await supabase.from("profiles").select("id, username, full_name, avatar_url").in("id", ids);
      setMembers(profs ?? []);
      const other = (profs ?? []).find((p: any) => p.id !== user.id);
      if (other) setTitle(other.full_name || other.username);
      await supabase.from("conversation_members").update({ last_read_at: new Date().toISOString() }).eq("conversation_id", id).eq("user_id", user.id);
      setLoading(false);
    })();

    const ch = supabase
      .channel(`conv-${id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages", filter: `conversation_id=eq.${id}` },
        (payload) => setMsgs((prev) => [...prev, payload.new as Msg]))
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [id, user?.id]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [msgs.length]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim() || !user) return;
    const body = text.trim();
    setText("");
    await supabase.from("messages").insert({ conversation_id: id, sender_id: user.id, body });
  };

  if (!user) return <AppShell><div className="py-20 text-center"><Link to="/auth" className="text-primary">Sign in to view messages</Link></div></AppShell>;

  return (
    <AppShell>
      <div className="mx-auto flex h-[calc(100vh-12rem)] max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-card">
        <header className="flex items-center gap-3 border-b border-border px-4 py-3">
          <Button asChild variant="ghost" size="icon"><Link to="/messages"><ArrowLeft className="h-4 w-4" /></Link></Button>
          <div className="font-display font-semibold">{title}</div>
        </header>

        <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
          {loading ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : msgs.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Say hello.</p>
          ) : msgs.map((m) => {
            const mine = m.sender_id === user.id;
            const sender = members.find((p) => p.id === m.sender_id);
            return (
              <div key={m.id} className={cn("flex gap-2", mine ? "justify-end" : "justify-start")}>
                {!mine && (
                  <Avatar className="h-7 w-7 self-end">
                    <AvatarImage src={sender?.avatar_url} />
                    <AvatarFallback className="text-[10px]">{(sender?.username?.[0] ?? "?").toUpperCase()}</AvatarFallback>
                  </Avatar>
                )}
                <div className={cn("max-w-[75%] rounded-2xl px-3 py-2 text-sm",
                  mine ? "bg-primary text-primary-foreground" : "bg-muted text-foreground")}>
                  {m.body}
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={send} className="flex items-center gap-2 border-t border-border p-3">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a message…" autoFocus />
          <Button type="submit" size="icon" disabled={!text.trim()}><Send className="h-4 w-4" /></Button>
        </form>
      </div>
    </AppShell>
  );
}
